import React, { cloneElement } from 'react';
import uuid from 'uuid-random';
import { pure, withProps } from 'recompose';
import { compose, pick, pickBy } from 'ramda';
import { debounce, throttle } from 'underscore';
import { useDeviceSelector } from 'js/store/hooks';
import { clsb, clsx } from 'js/utils/classNames.util';
import { scrollService } from 'js/services/scroll.service';
import { withTranslations } from 'js/components/hocs/withTranslations';

import withSwipe from './withSwipe';
import { useSliderNavigation } from './hooks';

import style from './index.scss';

export const EASE_OUT_QUINT = t => 1 + --t * t * t * t * t; // eslint-disable-line
export const LINEAR = t => t;

export class SlidesAnimation extends React.Component {
    constructor(props) {
        super(props);

        this.slider = null;
        this.renderAll = props.renderAll;
        this.lockId = uuid();

        const {
            options,
            children,
            activeSlide,
            slideWidth = 100,
            infinite,
            isMirrorMode
        } = props;
        const { animation } = options;
        const { type } = animation;

        this.animationTime = type === 'monoSlider' ? 500 : 0;
        this.animationTypeFn = type === 'monoSlider' ? EASE_OUT_QUINT : LINEAR;
        this.rotateTimer = null;
        this.rotateFrame = null;
        this.animateFrame = null;

        const animationType = {
            fading: type === 'fading',
            monoSlider: type === 'monoSlider',
            none: type === 'none' || type !== 'fading' && type !== 'monoSlider'
        };
        const isInfiniteMonoSlider = infinite && type === 'monoSlider' && children.length > 1;

        this.state = {
            tabKeyDown: false,
            slideWidth,
            animationType,
            translateX: SlidesAnimation.getTranslateX({
                isInfiniteMonoSlider, animationType, activeSlide, slideWidth, isMirrorMode
            }),
            childrenCount: children.length,
            isInfiniteMonoSlider,
            isSliderInFocus: false,
            navTabIndex: -1,
            isAnimating: false,
            isOverlayVisible: false,
            slideGapInPercents: 0
        };
    }

    setSliderData = (el) => {
        const { sliderRef, activeSlide, options: { slideGap }, isMirrorMode } = this.props;
        const { isInfiniteMonoSlider, animationType, slideWidth } = this.state;
        const slideGapInPercents = slideGap / el?.offsetWidth * 100 || 0;

        // in order to not focus on cloned edge elements of slider by TAB click
        if (isInfiniteMonoSlider && el?.children.length) {
            el.children[0].inert = true;
            el.children[el.children.length - 1].inert = true;
        }

        if (sliderRef) {
            sliderRef(el);
        }

        this.slider = el;
        this.setState({
            slideGapInPercents,
            translateX: SlidesAnimation.getTranslateX({
                slideWidth, activeSlide, animationType, slideGapInPercents, isInfiniteMonoSlider, isMirrorMode
            })
        });
    };

    static getDerivedStateFromProps(props, state) {
        const { isSwipeEnabled, swipeStarted, activeSlide, slidesCount, infinite, isMirrorMode } = props;
        const { animationType, slideWidth, childrenCount, isInfiniteMonoSlider, slideGapInPercents } = state;
        let { swipeDeltaX } = props;

        if (isSwipeEnabled && swipeStarted && swipeDeltaX && animationType.monoSlider) {
            const slide = infinite ? activeSlide + 1 : activeSlide;
            const startX = isMirrorMode ? slide * slideWidth : slide * -slideWidth;
            const slideShift = activeSlide * slideGapInPercents + (isInfiniteMonoSlider ? slideGapInPercents : 0);

            if (SlidesAnimation.isOverflowDenied({
                swipeDeltaX,
                activeSlide,
                slidesCount,
                infinite,
                isMirrorMode
            })) {
                swipeDeltaX = 0;
            }

            return {
                ...state,
                translateX: startX + (isMirrorMode ? slideShift : -slideShift) - swipeDeltaX / (window.innerWidth / slideWidth)
            };
        }

        if (props.children.length !== childrenCount) {
            return {
                ...state,
                translateX: SlidesAnimation.getTranslateX({
                    isInfiniteMonoSlider, animationType, activeSlide: 0, slideWidth, slideGapInPercents, isMirrorMode
                }),
                childrenCount: props.children.length
            };
        }

        return null;
    }

    static getTranslateX({ isInfiniteMonoSlider, animationType, activeSlide, slideWidth, slideGapInPercents = 0, isMirrorMode }) {

        if (animationType.fading) {
            return 0;
        }

        const activeSlideNumber = isInfiniteMonoSlider
            ? activeSlide + 1
            : activeSlide;

        return -(activeSlideNumber * slideGapInPercents + slideWidth * activeSlideNumber) * (isMirrorMode ? -1 : 1);
    }

    componentDidUpdate(prevProps, prevState) {
        const {
            autoRotate,
            activeSlide,
            prevSlide,
            slidesCount,
            sliderMoveRight,
            sliderMoveLeft,
            direction,
            infinite,
            isMirrorMode
        } = prevProps;
        const { activeSlide: currentSlide, renderAll, slideWidth = 100, setIsMirrorMode } = this.props;
        const { animationType, isAnimating, isSliderInFocus, isInfiniteMonoSlider } = this.state;

        this.slider && setIsMirrorMode(this.slider);
        this.handleOverlayVisibility();

        if (activeSlide !== currentSlide || renderAll === true) {
            this.renderAll = true;
        }

        if (!prevState.isSliderInFocus && isSliderInFocus && !isAnimating) {
            const activeSlideNumber = !isInfiniteMonoSlider ? currentSlide : currentSlide + 1;

            // in order to avoid looping by navigating via TAB button
            // we need to focus on the 0th element inside the 1st slide
            // and on 1st element inside all other slides
            scrollService.disableBodyScroll(this.lockId);
            this.slider.children[activeSlideNumber].children[currentSlide !== 0 ? 1 : 0].focus({ preventScroll: true });
            scrollService.enableBodyScroll(this.lockId);
        }

        if (isMirrorMode !== this.props.isMirrorMode) {
            this.setState({
                translateX: SlidesAnimation.getTranslateX({
                    isInfiniteMonoSlider, animationType, activeSlide, slideWidth, isMirrorMode: this.props.isMirrorMode
                })
            });
        }

        const { isSwipeEnabled, swipeStarted, swipeDeltaX } = this.props;

        if (isSwipeEnabled && swipeStarted && swipeDeltaX) {
            clearTimeout(this.rotateTimer);
            window.cancelAnimationFrame(this.rotateFrame);
            window.cancelAnimationFrame(this.animateFrame);
        } else if (activeSlide === currentSlide && activeSlide !== prevSlide) {
            if (prevProps.isSwipeEnabled && !SlidesAnimation.isOverflowDenied({
                swipeDeltaX: prevProps.swipeDeltaX,
                activeSlide,
                slidesCount,
                infinite,
                isMirrorMode: this.props.isMirrorMode
            })) {
                if (prevProps.swipeDeltaX > 0) {
                    sliderMoveRight();
                }

                if (prevProps.swipeDeltaX < 0) {
                    sliderMoveLeft();
                }
            }
        } else {
            if (!animationType.fading && direction && this.slider?.children?.length > 1 && activeSlide !== prevSlide) {
                this.animate(this.props);
            }

            if (autoRotate && !isSliderInFocus) {
                this.rotate();
            }
        }
    }

    setContainerWidth = (isUpdateTranslateX = true) => {
        this.slider.style.width = '';
        const { activeSlide, options: { slideGap }, isMirrorMode } = this.props;
        const { isInfiniteMonoSlider, animationType, slideWidth } = this.state;
        const containerWidth = this.slider.offsetWidth;

        if (containerWidth) {
            const slideGapInPercents = slideGap / containerWidth * 100;

            this.slider.style.width = `${containerWidth}px`;
            this.setState({
                slideGapInPercents,
                ...isUpdateTranslateX && {
                    translateX: SlidesAnimation.getTranslateX({
                        isInfiniteMonoSlider, animationType, activeSlide, slideWidth, slideGapInPercents, isMirrorMode
                    })
                }
            });
        }
    };

    handleOverlayVisibility = () => {
        const { swipeStarted, swipeDeltaX } = this.props;

        if (!swipeStarted && this.state.isOverlayVisible) {
            this.setState({
                isOverlayVisible: false
            });

            return;
        }

        // This is important!
        // swipeStarted property will be set on onClick event before any movement, so swipeDelta will still be 0.
        // Also, we can not rely on swipeDelta !== 0, because we can move carousel back to original position.
        // So, this condition means that if swipe started - we are waiting for property swipeStarted to be false to hide overlay
        if (this.state.isOverlayVisible) {
            return;
        }

        // initial swipe start - show overlay
        if (this.state.isOverlayVisible !== swipeStarted && swipeDeltaX !== 0) {
            this.setState({
                isOverlayVisible: swipeStarted && swipeDeltaX !== 0
            });
        }
    };

    throttledSetContainerWidth = throttle(() => {
        const { browserName, os } = this.props;

        (os !== 'ios' || browserName !== 'safari') && this.setContainerWidth();
    }, 50);

    debouncedSetContainerWidth = debounce(() => {
        const { browserName, os } = this.props;

        os === 'ios'
        && (browserName === 'chrome' || browserName === 'safari')
        && this.setContainerWidth();
    }, 250);

    componentDidMount() {
        window.addEventListener('resize', this.throttledSetContainerWidth);
        window.addEventListener('orientationchange', this.debouncedSetContainerWidth);
        document.body.addEventListener('click', this.handleSliderFocusChange);
        document.body.addEventListener('focusin', this.handleSliderFocusChange);

        if (this.props.autoRotate) {
            this.rotate();
        }
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.throttledSetContainerWidth);
        window.removeEventListener('orientationchange', this.debouncedSetContainerWidth);
        document.body.removeEventListener('click', this.handleSliderFocusChange);
        document.body.removeEventListener('focusin', this.handleSliderFocusChange);

        clearTimeout(this.rotateTimer);
        window.cancelAnimationFrame(this.rotateFrame);
        window.cancelAnimationFrame(this.animateFrame);
    }

    static isOverflowDenied({ swipeDeltaX, activeSlide, slidesCount, infinite, isMirrorMode }) {
        return !infinite
            && (
                !isMirrorMode && swipeDeltaX > 0 && activeSlide === slidesCount - 1 ||
                !isMirrorMode && swipeDeltaX < 0 && activeSlide === 0 ||
                isMirrorMode && swipeDeltaX < 0 && activeSlide === slidesCount - 1 ||
                isMirrorMode && swipeDeltaX > 0 && activeSlide === 0
            );
    }

    handleSliderFocusChange = (event) => {
        const { autoRotate } = this.props;
        const isSliderInFocus = this.slider?.contains(event.target);

        this.setState({ isSliderInFocus });

        if (autoRotate) {
            !isSliderInFocus && this.rotate();
            isSliderInFocus && clearTimeout(this.rotateTimer);
        }
    };

    scrollX(fromX, toX, startTime) {
        const { activeSlide, isMirrorMode } = this.props;
        const { isInfiniteMonoSlider, isSliderInFocus, slideGapInPercents } = this.state;
        const currentTime = Date.now();
        const durationTime = (currentTime - startTime) / this.animationTime;
        const time = durationTime < 1 ? durationTime : 1;
        const easeTime = this.animationTypeFn(time);
        const activeSlideNumber = !isInfiniteMonoSlider ? activeSlide : activeSlide + 1;
        const slideShift = activeSlideNumber * slideGapInPercents;

        if (isMirrorMode) {
            this.setState({
                translateX: easeTime * (toX - fromX) + (fromX + slideShift)
            });
        } else {
            this.setState({
                translateX: easeTime * (toX - fromX) + (fromX - slideShift)
            });
        }

        if (time < 1) {
            this.animateFrame = window.requestAnimationFrame(() => {
                this.scrollX(fromX, toX, startTime);
            });
        } else {
            window.cancelAnimationFrame(this.animateFrame);
            this.setState({ isAnimating: false });
            isSliderInFocus && this.slider.children[activeSlideNumber]?.children[activeSlide !== 0 ? 1 : 0].focus();
        }
    }

    rotate() {
        const { options, sliderMoveRight } = this.props;
        const { animation } = options;

        clearTimeout(this.rotateTimer);
        window.cancelAnimationFrame(this.rotateFrame);

        this.rotateTimer = setTimeout(() => {
            this.rotateFrame = window.requestAnimationFrame(() => {
                sliderMoveRight();
            });
        }, animation.displayTime * 1000);
    }

    animate(nextProps) {
        const { prevSlide, activeSlide, slidesCount, direction, infinite, isMirrorMode } = nextProps;
        const { slideWidth, animationType, slideGapInPercents } = this.state;
        const startTime = Date.now();
        let fromX;
        let toX;
        let deltaX = 0;

        this.setContainerWidth(false);
        window.cancelAnimationFrame(this.animateFrame);

        if (prevSlide !== activeSlide) {
            this.animateFrame = window.requestAnimationFrame(() => {
                if (animationType.monoSlider) {
                    if (!infinite) {
                        if (isMirrorMode) {
                            fromX = prevSlide * slideWidth;
                            toX = activeSlide * slideWidth;
                        } else {
                            fromX = prevSlide * -slideWidth;
                            toX = activeSlide * -slideWidth;
                        }
                    } else if (prevSlide === slidesCount - 1 && activeSlide === 0) {
                        if (direction === 'left') {
                            if (isMirrorMode) {
                                fromX = 0;
                                toX = slideWidth;
                                deltaX = slideWidth;
                            } else {
                                fromX = slidesCount * -slideWidth;
                                toX = -slideWidth;
                                deltaX = slidesCount * slideWidth;
                            }
                        } else {
                            if (isMirrorMode) {
                                fromX = slidesCount * slideWidth;
                                toX = slideWidth;
                                deltaX = slidesCount * slideWidth;
                            } else {
                                fromX = 0;
                                toX = -slideWidth;
                                deltaX = slidesCount * -slideWidth;
                            }
                        }
                    } else if (prevSlide === 0 && activeSlide === slidesCount - 1) {
                        if (direction === 'right') {
                            if (isMirrorMode) {
                                fromX = (slidesCount + 1) * slideWidth;
                                toX = slidesCount * slideWidth;
                                deltaX = slideWidth;
                            } else {
                                fromX = -slideWidth;
                                toX = slidesCount * -slideWidth;
                                deltaX = slidesCount * -slideWidth;
                            }
                        } else {
                            if (isMirrorMode) {
                                fromX = slideWidth;
                                toX = slidesCount * slideWidth;
                                deltaX = (slidesCount - 1) * slideWidth;
                            } else {
                                fromX = (slidesCount + 1) * -slideWidth;
                                toX = slidesCount * -slideWidth;
                                deltaX = slidesCount * slideWidth;
                            }
                        }
                    } else {
                        if (isMirrorMode && infinite) {
                            fromX = (prevSlide + 1) * slideWidth;
                            toX = (activeSlide + 1) * slideWidth;
                        } else if (isMirrorMode && !infinite) {
                            fromX = prevSlide * slideWidth;
                            toX = activeSlide * slideWidth;
                        } else if (!isMirrorMode) {
                            fromX = (prevSlide + 1) * -slideWidth;
                            toX = (activeSlide + 1) * -slideWidth;
                        }
                    }

                    const translateDelta = this.state.translateX - (slideGapInPercents * activeSlide - activeSlide) - deltaX;

                    if (isMirrorMode && infinite) {
                        if (prevSlide === 0 && activeSlide === slidesCount - 1) {
                            fromX = fromX + translateDelta;
                        }
                        if (prevSlide === slidesCount - 1 && activeSlide === 0) {
                            fromX = translateDelta - (slidesCount - 1) * deltaX;
                        }
                    } else {
                        fromX = fromX > toX
                            ? translateDelta < fromX ? translateDelta : fromX
                            : translateDelta > fromX ? translateDelta : fromX;
                    }
                } else {
                    fromX = prevSlide * -slideWidth;
                    toX = activeSlide * -slideWidth;
                }

                this.setState({ isAnimating: true });
                this.scrollX(fromX, toX, startTime);
            });
        }
    }

    handleOnKeyUp(event, sliderMove) {
        const { key, shiftKey } = event;
        const { animationType } = this.state;

        if (key === 'Tab' && !shiftKey) {
            sliderMove();
            this.setState({ navTabIndex: -1 });

            if (animationType.fading) {
                this.slider.children[this.props.activeSlide + 1]?.children[0].focus();
            }
        }

        if (key === 'Tab' && shiftKey) {
            sliderMove();
            this.setState({ navTabIndex: -1 });

            if (animationType.fading) {
                this.slider.children[this.props.activeSlide - 1]?.children[0].focus();
            }
        }
    }

    handleSliderKeyDown = (event) => {
        const { key } = event;

        if (this.state.tabKeyDown && key === 'Tab') {
            event.preventDefault();

            return;
        }

        if (key === 'Tab') {
            this.setState({ navTabIndex: 0, tabKeyDown: true });
        }
    };

    handleSliderKeyUp = (event) => {
        const { key } = event;

        if (key === 'Tab') {
            this.setState({ tabKeyDown: false });
        }
    };

    render() {
        const {
            options = {},
            activeSlide,
            classNames = {},
            styles = {},
            children,
            slideRef,
            swipeStart,
            handlerSliderTransitionEnd,
            isAllItemsVisible,
            isTouchDevice,
            sliderMoveRight,
            sliderMoveLeft,
            slidesCount
        } = this.props;
        const { height, slideGap } = options;
        const { animationType, translateX, isInfiniteMonoSlider, isOverlayVisible, navTabIndex } = this.state;
        const { fading } = animationType;
        const inlineStyle = pickBy(
            val => val != null,
            {
                ...translateX && {
                    transform: `translateX(${translateX}%)`,
                    WebkitTransform: `translateX(${translateX}%)`
                },
                height,
                ...styles.slider
            });
        const slideClsName = `${style.slide} ${classNames.slide || ''} ${fading ? style.fading : ''}`;
        return (
            <div
                onKeyUp={this.handleSliderKeyUp}
                onKeyDown={this.handleSliderKeyDown}
                onBlur={() => this.setState({ navTabIndex: -1 })}
                className={`${style.container} ${classNames.sliderContainer || ''}`}
                {...isTouchDevice ? { onTouchStart: swipeStart } : { onMouseDown: swipeStart }}
            >
                <div className={clsx([
                    clsb([style, classNames], ['sliderOverlay']),
                    isOverlayVisible ? '' : 'hidden'
                ])}
                    {...this.slider && { style: { height: `${this.slider.offsetHeight}px` } }}
                />
                <ul
                    className={`${style.slider} ${classNames.slider || ''}`}
                    onTransitionEnd={handlerSliderTransitionEnd}
                    ref={this.setSliderData}
                    style={inlineStyle}
                >
                    {isInfiniteMonoSlider && (
                        <li
                            className={slideClsName}
                            style={{ '--slide-gap': `${slideGap}px` }}
                        >
                            {cloneElement(children[children.length - 1], children[children.length - 1].props)}
                        </li>
                    )}
                    {(this.renderAll
                            ? children
                            : children.slice(0, 2)
                    ).map((child, index) => (
                        <li
                            key={child.key}
                            ref={index === 0 ? slideRef : null}
                            style={{ '--slide-gap': `${slideGap}px` }}
                            aria-hidden={isAllItemsVisible ? false : index !== activeSlide}
                            className={clsx([
                                slideClsName,
                                fading && index === activeSlide && style.fadingActive,
                                !isAllItemsVisible && index !== activeSlide && style.visibilityHidden
                            ])}
                        >
                            {index === activeSlide && activeSlide !== 0 && (
                                <span
                                    role="generic"
                                    tabIndex={navTabIndex}
                                    className={style.slideNav}
                                    onKeyUp={(event) => this.handleOnKeyUp(event, sliderMoveLeft)}
                                />
                            )}
                            {child}
                            {index === activeSlide && slidesCount !== activeSlide + 1 && (
                                <span
                                    tabIndex={navTabIndex}
                                    className={style.slideNav}
                                    onKeyUp={(event) => this.handleOnKeyUp(event, sliderMoveRight)}
                                />
                            )}
                        </li>
                    ))}
                    {isInfiniteMonoSlider && (
                        <li className={slideClsName}>
                            {cloneElement(children[0], children[0].props)}
                        </li>
                    )}
                </ul>
            </div>
        );
    }
}

export default compose(
    pure,
    withSwipe,
    withTranslations,
    withProps(props => ({
        ...useDeviceSelector(pick(['browserName', 'os', 'isTouchDevice'])),
        ...useSliderNavigation(props)
    }))
)(SlidesAnimation);
